// M005-F0052 (M005-D005 §4/§5) — Admin/Manager Bookings Hub shell.
//
// New page at /app/bookings — genuinely separate from the simple member
// view: this file NEVER imports src/components/event-simple/, and
// EventSimpleDashboard.js / EventSimple.css / the /app/event-simple route
// are untouched by design (owner ruling 2026-09-07, §1.2/§2). The pure
// helpers the Upcoming/Past tabs need were ported by direct copy per §7.2
// (recurrenceParser.js's expandOccurrences/describeSeriesPattern, appended
// verbatim to the existing main-side module; bookingStatus.js and the
// grouping module, copied into THIS directory as bookingStatus.js /
// bookingsGrouping.js).
//
// Tab set is permission-derived (§5, WEB-BKHUB-001): Upcoming and Past —
// the actor's own bookings, one card per series exactly as M009-F014
// specifies for the simple view — always render; Studio and Calendar render
// only when the SELECTED organisation's my-permissions projection grants at
// least one of bookings_manage_all / pricing_plans_view /
// pricing_booking_reprice (hasBookingsHubPermission — never role names).
// The nav entry is gated the same way, in Layout.js (§4.3, WEB-BKHUB-002).
//
// Studio is no longer a gap (M005-F0053, WebUI #75): StudioTab.js below
// mounts the org-wide list + filters + inline Approve/Decline. The
// New-booking entry (M005-F0056, WEB-BKHUB-010) is mounted below from
// NewBookingEntry.js; the price-plan panel (M005-F0054) mounts as a
// SIBLING of StudioTab below. The Calendar tab (M005-F0055, WebUI #77) is
// mounted below from BookingsCalendar.js.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  bookingApi,
  getMyEffectiveOrganisationPermissionsDetailed,
  userApi,
} from '../../services/api';
import {
  formatNextHeadline,
  formatOccurrenceRow,
  formatSeriesHeadline,
  formatSingleBookingWhen,
  groupMyBookings,
} from './bookingsGrouping';
import {
  hasBillingTabPermission,
  hasBookingsHubStudioPermission,
  isBillingPipelineBackendAvailable,
} from './bookingsHubPermissions';
// M005-F0067 (§7 amendment, WEB-BKHUB-022) — the shared Edit-booking link
// every card surface renders (Upcoming/Past cards here; Studio cards, the
// Calendar day lists and the shared drawer in their own files).
import {
  BOOKING_CARD_EDIT_LINK_TESTID,
  BOOKING_CARD_EDIT_LINK_TEXT,
  editBookingHref,
} from './bookingEditLink';
import StudioTab from './StudioTab';
import NewBookingEntry from './NewBookingEntry';
// M004-F0680 (WebUI #108, M005-F0054 amendment): the stopgap per-booking
// price-plan panel (PricePlanStudioMount: a booking <select> + in-panel dialog
// + second confirm) is RETIRED. Each Studio card opens the shared booking
// DRAWER, whose Price section is the one per-booking price surface (M004-F0678).
// M005-F0055 (WebUI #77): the Calendar tab panel, mounted below — the hub
// file itself carries only this import and the mount slot (§8).
import BookingsCalendar from './BookingsCalendar';
import { isMemberPricePlansBackendAvailable } from '../../utils/priceFeatureFlags';
// M005-F0070 (M005-D005 §4.1 amendment, owner ruling 2026-09-13): the
// "Price plans" tab panel — org-wide plan table, selector dropdown, editor,
// add/delete, grandfather-aware save. Mounted below, after Calendar.
import PricePlansHubTab from './PricePlansHubTab';
// M004-F0634 (§0a): the Billing tab panel — the ONE list (needs/approved/
// billed/problems, person groups, bulk approve), the drawer, the check
// actions. Mounted below, last.
import BillingTab from './BillingTab';
import './BillingTab.css';
import Layout from '../layout/Layout';
import './BookingsHub.css';

const responseList = (response) => response?.data?.data ?? response?.data ?? [];

export const TAB_UPCOMING = 'upcoming';
export const TAB_PAST = 'past';
export const TAB_STUDIO = 'studio';
export const TAB_CALENDAR = 'calendar';
// M005-F0070 (M005-D005 §4.1 amendment, owner ruling 2026-09-13) — the
// "Price plans" tab, after Calendar, gated on pricing_plans_view alone.
export const TAB_PRICE_PLANS = 'price-plans';
// M004-F0634 (§1/§2) — the Billing tab, LAST in the strip (Upcoming · Past
// · Studio · Calendar · Price plans · Billing), gated on the SELECTED org's
// ledger_dispatch_approve alone — the same my-permissions read the hub
// already performs; no second fan-out. A holder of ONLY this key gets the
// hub nav entry (BOOKINGS_HUB_PERMISSION_KEYS) and this tab — and nothing
// else.
//
// M004-F0634 §10 (2026-09-24 incident amendment): AND, since that same
// date, isBillingPipelineBackendAvailable() — the permission gates WHO,
// the flag gates WHETHER Core can serve the tab at all (default OFF; Core
// !695 unmerged). See bookingsHubPermissions.js and tabsForPermissions
// below.
export const TAB_BILLING = 'billing';

// M004-F0634 §1/§0a.1 — params only the Billing deep link carries (the
// /app/ledgerApprovals redirect target names the §0a view now; stage/
// occurrence are the superseded board's vocabulary, scrubbed if a stale
// link still carries them). Cleared when the strip leaves Billing so they
// never leak onto another tab's URL.
const BILLING_ONLY_URL_PARAMS = ['stage', 'view', 'occurrence', 'posting'];

// §5: Upcoming/Past always render for anyone who can reach the hub at all;
// Studio/Calendar hide without the selected org's ORIGINAL hub keys (a
// ledger-only holder never sees booking-management chrome, F0634 §2). The
// Price plans tab renders only for an EXPLICIT pricing_plans_view; the
// Billing tab (M004-F0634) renders only for an EXPLICIT
// ledger_dispatch_approve — fail closed like every other permission read
// here.
export const tabsForPermissions = (permissions) => {
  const tabs = [
    { key: TAB_UPCOMING, label: 'Upcoming' },
    { key: TAB_PAST, label: 'Past' },
  ];
  if (hasBookingsHubStudioPermission(permissions)) {
    tabs.push({ key: TAB_STUDIO, label: 'Studio' });
  }
  // M004-F0717 (design 15.7): Calendar is admitted by the Studio keys OR by billing_view (a billing.view-only member
  // sees the transaction calendar and nothing of the booking-management chrome). Explicit true only, fail closed.
  if (hasBookingsHubStudioPermission(permissions) || permissions?.billing_view === true) {
    tabs.push({ key: TAB_CALENDAR, label: 'Calendar' });
  }
  if (permissions?.pricing_plans_view === true) {
    tabs.push({ key: TAB_PRICE_PLANS, label: 'Price plans' });
  }
  // M004-F0634 §10 (2026-09-24 incident): permission alone is no longer
  // sufficient — the tab also needs the backend to actually exist
  // (isBillingPipelineBackendAvailable, default OFF until Core !695 ships).
  // A holder with the permission but the flag off gets NO Billing tab; the
  // ?tab=billing deep link (below, and the /app/ledgerApprovals redirect)
  // then falls back to Upcoming exactly as it does for a denied holder.
  if (hasBillingTabPermission(permissions) && isBillingPipelineBackendAvailable()) {
    tabs.push({ key: TAB_BILLING, label: 'Billing' });
  }
  return tabs;
};

// M005-F0052 amendment (WebUI #107): the hub's organisation list is the
// caller's MEMBERSHIPS (GET /users/me/organisations). Every hub surface is
// gated on GET /organisations/:id/my-permissions, which Core answers only
// for an active membership (404 otherwise). GET /organisations/mine was the
// wrong source: for organisation_view_all it lists EVERY organisation with
// no membership fields, so the name-sorted first one was usually an org the
// caller is not a member of — my-permissions 404, the Price plans tab (and
// Studio/Calendar) hidden behind "Could not load your permissions".
// Normalised to the { id, name, is_primary, user_role } shape the selector
// below reads.
export const hubMembershipOrganisations = (response) => {
  const list = response?.data?.data?.organisations;
  return (Array.isArray(list) ? list : [])
    .filter((row) => row && row.organisation_id !== undefined && row.organisation_id !== null)
    .map((row) => ({
      id: row.organisation_id,
      name: row.organisation_name,
      is_primary: row.is_primary,
      user_role: row.role,
    }));
};

// Primary organisation first, then by name — the same getMine→primary
// default the BookingKeeperDashboard / MusicChoreography loaders use.
export const selectHubOrganisations = (orgList) => {
  const list = (Array.isArray(orgList) ? orgList : [])
    .filter((org) => org && (org.id ?? org.organisation_id) !== undefined);
  return [...list].sort((a, b) => {
    if (Boolean(b.is_primary) !== Boolean(a.is_primary)) return b.is_primary ? 1 : -1;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
};

const organisationIdOf = (org) => org?.id ?? org?.organisation_id;

const BookingCard = ({ card }) => (
  <article className="bkh-card" data-testid="bkh-booking-card" data-kind={card.kind}>
    <div className="bkh-card-main">
      <span className={card.statusBadgeClass || 'status-badge'}>{card.statusText}</span>
      <p className="bkh-card-when">
        {card.kind === 'series'
          ? formatSeriesHeadline(card)
          : formatSingleBookingWhen(card.start, card.end)}
      </p>
      <p className="bkh-card-next">{formatNextHeadline(card) || ''}</p>
      <p className="bkh-card-room">{card.roomName}</p>
      {/* M005-F0067 (WEB-BKHUB-022): a series card links to its
          representative row (the series master), a single to itself. */}
      {card.id != null ? (
        <p className="bkh-card-edit">
          <Link
            className="bkh-card-edit-link"
            data-testid={BOOKING_CARD_EDIT_LINK_TESTID}
            to={editBookingHref(card.id)}
          >
            {BOOKING_CARD_EDIT_LINK_TEXT}
          </Link>
        </p>
      ) : null}
      {card.syncError ? <p className="bkh-card-sync-error">Sync error — this booking failed to reach the calendar.</p> : null}
    </div>
    {card.kind === 'series' && card.occurrences.length > 0 ? (
      <details className="bkh-card-dates">
        <summary>
          {`${card.occurrences.length} date${card.occurrences.length === 1 ? '' : 's'}`}
          {card.isTruncated ? ' (list truncated)' : ''}
        </summary>
        <ul>
          {card.occurrences.map((occurrence, index) => (
            <li key={`${occurrence.start.getTime()}-${index}`}>{formatOccurrenceRow(occurrence)}</li>
          ))}
        </ul>
      </details>
    ) : null}
  </article>
);

const CardSection = ({ cards, emptyMessage, testid }) => (
  <div data-testid={testid}>
    {cards.length === 0 ? (
      <p className="bkh-empty" data-testid={`${testid}-empty`}>{emptyMessage}</p>
    ) : (
      cards.map((card) => <BookingCard key={card.key} card={card} />)
    )}
  </div>
);

const BookingsHub = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [permissions, setPermissions] = useState({});
  // M004-F0634 §5 — a FAILED permissions read is a distinct, honest state:
  // "Could not load your permissions" with a retry, never the
  // no-permission text (the !342 api.js swallow class, fixed at the source
  // by getMyEffectiveOrganisationPermissionsDetailed).
  const [permissionsReadFailed, setPermissionsReadFailed] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // §9 (M005-F0056): refetch path the New-booking entry drives after a
  // successful create — the hub re-reads /api/bookings so the new booking
  // appears in the actor's own Upcoming tab immediately.
  const loadBookings = useCallback(async () => {
    const bookingsResponse = await bookingApi.getMyBookings().catch(() => null);
    setBookings(responseList(bookingsResponse));
    return bookingsResponse;
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    Promise.all([
      userApi.getMyOrganisations().catch(() => null),
      loadBookings(),
    ]).then(([orgResponse, bookingsResponse]) => {
      if (!current) return;
      const orgs = selectHubOrganisations(hubMembershipOrganisations(orgResponse));
      setOrganisations(orgs);
      // M004-F0634 §1 — the /app/ledgerApprovals redirect may carry the
      // org it was deep-linked with (?org=<id>): honour it when it is one
      // of the actor's organisations, else the primary default stands.
      const requestedOrgId = searchParams.get('org');
      const requested = requestedOrgId
        ? orgs.find((org) => String(organisationIdOf(org)) === String(requestedOrgId))
        : null;
      const primary = requested || orgs.find((org) => org.is_primary) || orgs[0];
      setSelectedOrgId(primary ? String(organisationIdOf(primary)) : '');
      if (!orgResponse) setError('Could not load your organisations.');
      if (!bookingsResponse) setError((prev) => `${prev} Could not load your bookings.`.trim());
      setLoading(false);
    });
    // The org preselect reads the link's parameters ONCE on load; later
    // param changes must not yank the selector away from the user.
  }, [loadBookings]);

  useEffect(() => {
    let current = true;
    if (!selectedOrgId) {
      setPermissions({});
      setPermissionsReadFailed(false);
      return () => { current = false; };
    }
    // Fail closed on any read failure: Studio/Calendar/Billing hide, never
    // guess — AND (F0634 §5) the failure is SHOWN as "Could not load your
    // permissions" with a retry, never dressed up as a denial.
    getMyEffectiveOrganisationPermissionsDetailed(selectedOrgId).then((resolved) => {
      if (!current) return;
      setPermissions(resolved?.permissions || {});
      setPermissionsReadFailed(resolved?.failed === true);
    });
    return () => { current = false; };
  }, [selectedOrgId]);

  // The active tab is URL-derived on ENTRY (F0634 §1/§5: /app/bookings?tab=billing
  // is a real, shareable entry — the redirect's target): a param naming an
  // admitted tab wins; anything else (absent, unknown, not admitted for
  // this org — e.g. ?tab=billing WITHOUT the key) falls back to Upcoming,
  // and the tab strip simply does not render the entry.
  //
  // Strip clicks are NOT deep links, so they do not get URLs: switching
  // tabs writes the URL only where the URL IS the feature — entering
  // Billing (?tab=billing keeps M004-F0634's deep-link surface reachable
  // from the strip) and leaving it (billing's stage/occurrence/posting
  // params must not leak onto other tabs). Every other switch — and
  // therefore the view around the shared drawer's Close (WEB-BKHUB-017's
  // close-preserves-URL contract) — leaves /app/bookings exactly as it
  // was. Boundary note: after a strip switch away from a URL-carried tab,
  // browser Back re-derives from the URL only when the strip has not been
  // used since; the last strip choice is the actor's word.
  const tabs = tabsForPermissions(permissions);
  const requestedTab = searchParams.get('tab');
  // M004-F0678: /app/bookings?booking=<id> is the deep link to ONE booking's
  // drawer. The drawer lives on the Studio tab, so the link enters there — but
  // only where the actor's permissions admit Studio at all (never a hidden tab).
  const focusBookingId = searchParams.get('booking');
  const studioAdmitted = tabs.some((tab) => tab.key === TAB_STUDIO);
  const urlTab = tabs.some((tab) => tab.key === requestedTab)
    ? requestedTab
    : (focusBookingId && studioAdmitted ? TAB_STUDIO : null);
  const [selectedTab, setSelectedTab] = useState(null);
  const activeTab = selectedTab ?? urlTab ?? TAB_UPCOMING;
  const selectTab = (key) => {
    setSelectedTab(key);
    if (key !== TAB_BILLING && activeTab !== TAB_BILLING) return;
    const next = new URLSearchParams(searchParams);
    if (key === TAB_BILLING) {
      next.set('tab', TAB_BILLING);
    } else {
      // Leaving billing: the billing deep-link params are billing's alone.
      next.delete('tab');
      BILLING_ONLY_URL_PARAMS.forEach((param) => next.delete(param));
    }
    setSearchParams(next, { replace: true });
  };

  const retryPermissionsRead = () => {
    // Re-runs the selected org's my-permissions read through the same
    // effect path (a fresh read, not a state flip).
    const orgId = selectedOrgId;
    setPermissionsReadFailed(false);
    getMyEffectiveOrganisationPermissionsDetailed(orgId).then((resolved) => {
      setPermissions(resolved?.permissions || {});
      setPermissionsReadFailed(resolved?.failed === true);
    });
  };

  const { upcoming, past } = groupMyBookings(bookings);
  const selectedOrg = organisations.find((org) => String(organisationIdOf(org)) === String(selectedOrgId));

  return (
    <Layout>
      <section className="bkh-hub" data-testid="bookings-hub-root">
        <header className="bkh-header">
          <h1>Bookings Hub</h1>
          {organisations.length > 1 ? (
            <label className="bkh-org-select">
              Organisation
              <select
                data-testid="bookings-hub-org-selector"
                value={selectedOrgId}
                onChange={(event) => setSelectedOrgId(event.target.value)}
              >
                {organisations.map((org) => (
                  <option key={organisationIdOf(org)} value={String(organisationIdOf(org))}>{org.name}</option>
                ))}
              </select>
            </label>
          ) : null}
        </header>

        {error ? <p className="bkh-error" role="alert" data-testid="bookings-hub-error">{error}</p> : null}

        {/* M004-F0634 §5 — a FAILED permissions read says exactly this, with
            a retry — never the no-permission text. Renders even when the
            read would have gated chrome the actor cannot see otherwise. */}
        {permissionsReadFailed ? (
          <p className="bkh-error" role="alert" data-testid="bookings-hub-permissions-error">
            Could not load your permissions.{' '}
            <button type="button" className="btn btn-outline btn-sm" data-testid="bookings-hub-permissions-retry" onClick={retryPermissionsRead}>
              Retry
            </button>
          </p>
        ) : null}

        <div className="bkh-tabs" role="tablist" aria-label="Bookings sections">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`bkh-tab${activeTab === tab.key ? ' active' : ''}`}
              data-testid={`bookings-hub-tab-${tab.key}`}
              onClick={() => selectTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* M005-F0056 (§9, WEB-BKHUB-010) — permission-gated New-booking entry.
            M004-F0719 (design 17.1, owner): it lives ONLY under Upcoming; it used to sit above every tab. */}
        {activeTab === TAB_UPCOMING ? (
          <NewBookingEntry permissions={permissions} onCreated={loadBookings} />
        ) : null}

        {loading ? (
          <p className="bkh-loading" data-testid="bookings-hub-loading">Loading…</p>
        ) : (
          <>
            {activeTab === TAB_UPCOMING ? (
              <CardSection
                cards={upcoming}
                emptyMessage="No upcoming bookings."
                testid="bookings-hub-upcoming"
              />
            ) : null}
            {activeTab === TAB_PAST ? (
              <CardSection
                cards={past}
                emptyMessage="No past bookings."
                testid="bookings-hub-past"
              />
            ) : null}
            {activeTab === TAB_STUDIO ? (
              <StudioTab
                organisationId={selectedOrgId}
                organisationName={selectedOrg?.name}
                permissions={permissions}
                focusBookingId={focusBookingId}
              />
            ) : null}
            {activeTab === TAB_CALENDAR ? (
              <BookingsCalendar
                selectedOrgId={selectedOrgId}
                organisationName={selectedOrg?.name || ''}
                permissions={permissions}
              />
            ) : null}
            {/* M005-F0070 (M005-D005 §4.1 amendment): the Price plans tab.
                Renders only when tabsForPermissions admitted it (explicit
                pricing_plans_view on the selected org); its mutating controls
                re-check pricing_plans_manage inside the component. */}
            {/* M004-F0646 (design §3.4): "New plan..." in the Users-drawer picker
                lands here with returnTo=/app/admin/users?user=<id>. Only an
                in-app users path is honoured (no open redirect). */}
            {activeTab === TAB_PRICE_PLANS && isMemberPricePlansBackendAvailable()
              && /^\/app\/admin\/users(\?|$)/.test(searchParams.get('returnTo') || '') ? (
                <p>
                  <Link to={searchParams.get('returnTo')} data-testid="price-plans-return-link">
                    &larr; Back to the user&apos;s price plans
                  </Link>
                </p>
              ) : null}
            {activeTab === TAB_PRICE_PLANS ? (
              <PricePlansHubTab
                selectedOrgId={selectedOrgId}
                organisationName={selectedOrg?.name || ''}
                permissions={permissions}
              />
            ) : null}
            {/* M004-F0634 (§0a): the Billing tab — the ONE list, the side
                drawer with the can-gated actions. Renders only when
                tabsForPermissions admitted it (explicit
                ledger_dispatch_approve on the selected org). */}
            {activeTab === TAB_BILLING ? (
              <BillingTab organisationId={selectedOrgId} />
            ) : null}
          </>
        )}
      </section>
    </Layout>
  );
};

export default BookingsHub;
